// ─── App root ──────────────────────────────────────────────────────────────────

import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import AppRoutes from './routes/AppRoutes';
import { RouteErrorBoundary } from './components/ui/ErrorBoundary';
import { useTheme } from './hooks/useTheme';
import { queryClient } from './api/queryClient';

const ThemedToaster: React.FC = () => {
  const { resolved } = useTheme();
  const isDark = resolved === 'dark';
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: {
          fontFamily: "'Inter', sans-serif",
          fontSize: '14px',
          borderRadius: '12px',
          background: isDark ? 'rgb(22 32 51)' : '#ffffff',
          color: isDark ? 'rgb(241 245 249)' : 'rgb(15 23 42)',
          border: isDark ? '1px solid rgb(30 41 59)' : '1px solid rgb(226 232 240)',
          boxShadow: isDark
            ? '0 16px 40px -8px rgba(0,0,0,0.6)'
            : '0 10px 25px -5px rgba(15,23,42,0.1)',
        },
        success: { iconTheme: { primary: '#10B981', secondary: '#fff' } },
        error:   { iconTheme: { primary: '#EF4444', secondary: '#fff' } },
      }}
    />
  );
};

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    {/*
      react-router wraps its location update in `startTransition` by default. With
      every page behind `React.lazy`, that meant: the URL changed immediately, the new
      route suspended, and React kept the PREVIOUS page committed rather than showing
      the Suspense fallback — so `useLocation()` still reported the old path and the
      sidebar kept highlighting the old nav item, with no spinner, for as long as the
      chunk took to download. Opting out makes the navigation a plain synchronous
      update, so the Suspense fallback actually renders while the chunk loads.
    */}
    <BrowserRouter unstable_useTransitions={false}>
      <AuthProvider>
        {/*
          Outer net. Every page is a `React.lazy` chunk; a rejected import throws during
          render, and with no boundary anywhere React unmounts the whole root — a blank
          white screen with no message and no way back. This catches those, including on
          the public routes, which render outside GlobalLayout and so have no inner
          boundary of their own. The toaster stays outside it and keeps working.
        */}
        <RouteErrorBoundary label="app shell">
          <AppRoutes />
        </RouteErrorBoundary>
        <ThemedToaster />
      </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
